import { useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { useEffect, useMemo } from 'react'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'
import { Textarea } from '@wartek-id/textarea'

import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaShared/components/Card'
import {
  API_PATH_PENGEMBANGAN_KOMPETENSI,
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIGetPengembanganKompetensi,
  useAPIGetRealisasiSKP,
  useAPIPelaksanaanProgress,
  useAPISubmitPengembanganKompetensi,
} from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'

import type {
  MultiPeriodPayload,
  ParamsSubmitPengembanganKompetensi,
} from 'types/kinerja'

type PengembanganKompetensiProps = {
  periodParams?: MultiPeriodPayload
}

const PengembanganKompetensi = (props: PengembanganKompetensiProps) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (props.periodParams) return props.periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [props.periodParams, periodNo, periodYear])

  const formMethods = useForm<
    Pick<
      ParamsSubmitPengembanganKompetensi,
      'reflectionImplementation' | 'reflectionInspiration'
    >
  >({
    mode: 'onChange',
    defaultValues: {
      reflectionInspiration: '',
      reflectionImplementation: '',
    },
  })

  // Get pelaksanaan progress data
  const { data: dataPelaksanaan, isLoading: isLoadingPelaksanaanProgress } =
    useAPIPelaksanaanProgress({
      periodParams: {
        skp_year: resolvedPeriodParams.skpYear,
        skp_period: resolvedPeriodParams.skpPeriod,
      },
      tipe: 'GPK',
      opts: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Get indikator data
  const { data: dataIndikator, isLoading: isLoadingIndikator } =
    useAPIGetRealisasiSKP({
      params: {
        kategori: ['PK'],
        periodParams: {
          skp_year: resolvedPeriodParams.skpYear,
          skp_period: resolvedPeriodParams.skpPeriod,
        },
        tipe: 'GPK',
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Get pengembangan kompetensi data
  const {
    data: dataPengembanganKompetensi,
    isLoading: isLoadingPengembanganKompetensi,
  } = useAPIGetPengembanganKompetensi({
    params: {
      tipe: 'GPK',
      periodParams: {
        skp_year: resolvedPeriodParams.skpYear,
        skp_period: resolvedPeriodParams.skpPeriod,
      },
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // Sync pengembangan kompetensi data
  useEffect(() => {
    if (!!dataPengembanganKompetensi) {
      // Set data from BE as initial form data
      formMethods.reset(dataPengembanganKompetensi?.data?.competencyImprovement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPengembanganKompetensi])

  const submitMutation = useAPISubmitPengembanganKompetensi()

  const isLoading =
    isLoadingPengembanganKompetensi || isLoadingPelaksanaanProgress

  // In the new documents flow (>=2025), user will only select one Pengembangan Kompetensi
  // via Perencanaan, so that we can safely display the first item
  const indikator = dataIndikator?.data?.[0]?.deskripsi

  const state = dataPengembanganKompetensi?.data?.state
  const isSubmitted = state === 'SUBMITTED' || state === 'APPROVED'
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isEvaluatedBeforeSubmitted = isEvaluated && !isSubmitted

  // Only show action buttons if the SKP is not evaluated
  // and the form is neither submitted nor approved.
  const hasActions = !isEvaluated && !isSubmitted && !isLoading

  const isFormDisabled = isEvaluatedBeforeSubmitted
  const isSubmitDisabled = !formMethods.formState.isValid

  const handleSubmit = () => {
    if (isEvaluated || isSubmitted || isSubmitDisabled) return

    submitMutation.mutate(
      {
        state: 'SUBMITTED',
        ...formMethods.getValues(),
        tipe: 'GPK',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_PENGEMBANGAN_KOMPETENSI)
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
          toast({
            message: 'Berhasil kirim ke atasan!',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal kirim ke atasan. Coba lagi.',
          })
        },
      }
    )
  }

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH,
  })

  return (
    <main className="min-h-full px-8 pt-12 pb-8 bg-gray-10">
      <PageGuardGpkPelaksanaanStatus periodParams={resolvedPeriodParams}>
        <div className="mx-auto max-w-7xl">
          <Header
            title="Pengembangan Kompetensi"
            className="mb-9 print:hidden"
            onBack={handleBack}
          />

          <div className="flex items-start gap-8">
            <div className="flex-grow-0 flex-shrink-0 space-y-4 w-96">
              <Card
                withBorder
                aria-live="polite"
                aria-busy={isLoadingIndikator}
              >
                <CardBody className="p-4 space-y-2">
                  <Text as="h2" variant="heading-sm">
                    Indikator pilihan Anda
                  </Text>
                  <hr className="border-black-20" />
                  {isLoadingIndikator ? (
                    <>
                      <Shimmer className="!w-full" />
                      <Shimmer className="!w-full" />
                      <Shimmer />
                    </>
                  ) : (
                    <Text color="subdued">{indikator || '-'}</Text>
                  )}
                </CardBody>
              </Card>

              <aside className="flex gap-2 p-4 border rounded-lg border-gray-50">
                <Icon fontSize="sm" className="!text-xl !leading-5">
                  info
                </Icon>

                <ul className="pl-6 space-y-2 list-disc">
                  <Text as="li">
                    <strong>Lakukan kegiatan</strong> yang berkaitan dengan
                    indikator pilihan Anda sebelum mengisi refleksi.
                  </Text>
                  <Text as="li">
                    <strong>Tidak perlu kumpulkan Bukti Dukung</strong> untuk
                    Pengembangan Kompetensi.
                  </Text>
                </ul>
              </aside>
            </div>

            <Card
              aria-busy={isLoading}
              aria-live="polite"
              className="w-full"
              withBorder
            >
              <CardHeader className="space-y-1">
                {isLoading ? (
                  <Shimmer height="32px" width="100%" />
                ) : isEvaluatedBeforeSubmitted ? (
                  <div className="flex items-center space-x-2">
                    <Icon color="informational">info</Icon>

                    <Text variant="heading-sm" as="h2">
                      Dokumen Refleksi tidak dapat diubah lagi karena Predikat
                      Kinerja sudah ditetapkan
                    </Text>
                  </div>
                ) : isSubmitted ? (
                  <div className="flex items-center space-x-2">
                    <Icon color="success">check_circle</Icon>

                    <Text variant="heading-sm" as="h2">
                      Dokumen Refleksi sudah dikirim ke Tim Kinerja
                    </Text>
                  </div>
                ) : (
                  <>
                    <Text variant="heading-lg" as="h2">
                      Isi Refleksi
                    </Text>
                    <Text>
                      Anda dapat ubah pengisian secara berkala selama Penetapan
                      Predikat Kinerja belum dilakukan.
                    </Text>
                  </>
                )}
              </CardHeader>

              {isLoading ? (
                <CardBody className="p-6 space-y-4">
                  <Shimmer height={112} width="100%" />
                  <Shimmer height={112} width="100%" />
                </CardBody>
              ) : isSubmitted ? (
                <CardBody className="p-6">
                  <dl className="space-y-6">
                    <div className="space-y-2">
                      <Text variant="heading-sm" as="dt">
                        Apa inspirasi menarik yang Anda peroleh dari kegiatan?
                      </Text>
                      <Text as="dd" className="overflow-wrap-anywhere">
                        {formMethods.getValues('reflectionInspiration') || '-'}
                      </Text>
                    </div>

                    <div className="space-y-2">
                      <Text variant="heading-sm" as="dt">
                        Bagaimana Anda menerapkan inspirasi tersebut untuk
                        kemajuan penguasaan kompetensi?
                      </Text>
                      <Text as="dd" className="overflow-wrap-anywhere">
                        {formMethods.getValues('reflectionImplementation') ||
                          '-'}
                      </Text>
                    </div>
                  </dl>
                </CardBody>
              ) : (
                <CardBody className="p-6 space-y-4">
                  <fieldset className="space-y-2">
                    <div className="space-y-1">
                      <Text
                        variant="heading-sm"
                        as="label"
                        htmlFor="inspiration"
                      >
                        Apa inspirasi menarik yang Anda peroleh dari kegiatan?
                      </Text>
                      <Text id="inspirationDesc">
                        Ketik maksimal 3 inspirasi:
                      </Text>
                    </div>
                    <Textarea
                      aria-describedby="inspirationDesc"
                      className="h-20 !resize-y"
                      id="inspiration"
                      isDisabled={isFormDisabled}
                      maxLength={1000}
                      placeholder="Ketik di sini"
                      {...formMethods.register('reflectionInspiration', {
                        required: true,
                      })}
                    />
                  </fieldset>

                  <fieldset className="space-y-2">
                    <div className="space-y-1">
                      <Text
                        variant="heading-sm"
                        as="label"
                        htmlFor="implementaion"
                      >
                        Bagaimana Anda menerapkan inspirasi tersebut untuk
                        kemajuan penguasaan kompetensi?
                      </Text>
                      <Text id="implementationDesc">
                        Ketik minimal 1 penerapan untuk kemajuan penguasaan
                        kompetensi Anda:
                      </Text>
                    </div>

                    <Textarea
                      aria-describedby="implementationDesc"
                      className="h-20 !resize-y"
                      id="implementaion"
                      isDisabled={isFormDisabled}
                      maxLength={1000}
                      placeholder="Ketik di sini"
                      {...formMethods.register('reflectionImplementation', {
                        required: true,
                      })}
                    />
                  </fieldset>
                </CardBody>
              )}

              {hasActions && (
                <CardFooter
                  withSeparator={false}
                  className="flex justify-end pt-0 space-x-2"
                >
                  <Button
                    className="!px-16"
                    size="lg"
                    isLoading={isLoading}
                    disabled={
                      isFormDisabled ||
                      isSubmitDisabled ||
                      submitMutation.isLoading
                    }
                    onClick={handleSubmit}
                  >
                    {submitMutation.isLoading ? 'Mengirim...' : 'Kirim'}
                  </Button>
                </CardFooter>
              )}
            </Card>
          </div>
        </div>
      </PageGuardGpkPelaksanaanStatus>
    </main>
  )
}

export default PengembanganKompetensi
